<template>
  <Card>
    <template #content>
      <div class="mb-5">
        <h3 class="text-center">Active Inventory Summary By Node</h3>
      </div>
      <div class="grid grid-cols-12 mb-8">
        <div class="col-span-6 ml-1">
          <RMSAutoComplete
            :fieldId="'weeks-of-year-autocomplete'"
            :config="{ label: 'Week', optionLabel: 'display', showAvailable: false }"
            :loading="weeksOfYearLoading"
            :options="weeksOfYear"
            :originalValue="weekOfYearOriginalValue"
            @onSelected="onSelectWeekOfYearFilter"
            @onClear="onClearWeekOfYearFilter"
          />
        </div>
        <div class="col-span-6 ml-1 mr-1">
          <RMSAutoComplete
            ref="skuFilterRef"
            :fieldId="'sku-autocomplete'"
            :config="{ label: 'SKU', showAvailable: false }"
            :loading="skusLoading"
            :options="skus"
            @onSelected="onSelectSkuFilter"
            @onClear="onClearSkuFilter"
          />
        </div>
      </div>
      <h4 v-if="inventoryAggregateLoading" class="text-center m-8">Loading...</h4>
      <h4 v-else-if="!chartData.datasets[0]?.data.length" class="text-center m-8">No inventory data found</h4>
      <PieChart v-else :chartData="chartData" :options="chartOptions" :height="400" />
    </template>
  </Card>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)
import { computed, onMounted, ref, watch } from 'vue'
import { PieChart } from 'vue-chart-3'
import Card from "primevue/card"
import RMSAutoComplete from '@/components/fields/RMSAutoComplete.vue'
import ApiUtility from '@/utilities/ApiUtility'
import ActiveInventorySummaryByNodeService from '@/services/activity/historicalWeeklyInventory/ActiveInventorySummaryByNodeService'
import SkusService from '@/services/skus/SkusService'
import WeeksOfYearService from '@/services/WeeksOfYearService'
import formattingUtilities from '@/utilities/formattingUtilities'
import { DateTime } from 'luxon'

export default {
  components: {
    RMSAutoComplete,
    Card,
    PieChart
  },
  props: {
    program: {
      type: Object,
      default: () => {}
    }
  },
  setup(props) {

    const chartData = computed(() => ({
      labels: ['Customer', 'Network', 'Partner'],
      datasets: [{
        data: nodeInventory.value,
        backgroundColor: ['#79AF00', '#0079AF', '#FFB408']
      }]
    }))

    const nodeInventory = ref([])
    const chartOptions = ref()
    const inventoryAggregateApi = new ApiUtility()
    const inventoryAggregateLoading = ref(true)
    const skusApi = new ApiUtility()
    const skuFilterRef = ref()
    const skus = ref([])
    const skusLoading = ref(true)
    const weeksOfYearApi = new ApiUtility()
    const weekOfYearOriginalValue = ref({})
    const weeksOfYear = ref([])
    const weeksOfYearLoading = ref(true)

    onMounted(() => {
      weeksOfYearApi.init(new WeeksOfYearService(), {
        start_less_than_date: DateTime.fromJSDate(new Date()).minus({ week: 1 }).toSQLDate(),
      })
      weeksOfYearApi.setSelect(['id', 'week', 'year'].join())
      weeksOfYearApi.setParams({ sort_by: 'id', sort_dir: 'desc' })
      weeksOfYearApi.setGetAllEntries()
      weeksOfYearApi.loadData(handleWeeksOfYearApiResponse)

      skusApi.init(new SkusService(), {
        program_id: props.program.id,
      })
      skusApi.setSelect(['id', 'name', 'type'].join())
      skusApi.setParams({ sort_by: 'name', sort_dir: 'asc' })
      skusApi.setWith('components')
      skusApi.setGetAllEntries()
      skusApi.loadData(handleSkusApiResponse)

      inventoryAggregateApi.init(new ActiveInventorySummaryByNodeService(), {
        program_id: props.program.id,
      })
      inventoryAggregateApi.setGetAllEntries()
    })

    const handleInventoryAggregateApiResponse = (response) => {
      nodeInventory.value = []
      response.forEach((record) => {
        if (nodeInventory.value[record.location_type_id - 1] === undefined) {
          nodeInventory.value[record.location_type_id - 1] = 0
        }
        nodeInventory.value[record.location_type_id - 1] += parseInt(record.active)
      })
      inventoryAggregateLoading.value = false
    }

    const handleWeeksOfYearApiResponse = (response) => {
      weeksOfYear.value = response.data.map((woy) => {
        woy.display = formattingUtilities.concatenateWeekOfYear(woy)
        return woy
      })
      weekOfYearOriginalValue.value = weeksOfYear.value[0]
      weeksOfYearLoading.value = false
    }

    const handleSkusApiResponse = (response) => {
      skus.value = response.data
      skusLoading.value = false
    }

    watch(
      () => props.program,
      () => {
        skuFilterRef.value.resetSelected()
        skusApi.clearFilterNoLoad('program_id')
        skusApi.addFilterNoLoad('program_id', props.program.id)
        skusApi.loadData(handleSkusApiResponse)
        inventoryAggregateApi.clearFilterNoLoad(['sku_id', 'sku_id_in', 'program_id'])
        inventoryAggregateApi.addFilterNoLoad('program_id', props.program.id)
        loadData()
      }
    )

    const loadData = () => {
      inventoryAggregateLoading.value = true
      inventoryAggregateApi.loadData(handleInventoryAggregateApiResponse)
    }

    const onSelectWeekOfYearFilter = (woy) => {
      inventoryAggregateApi.addFilterNoLoad('week_of_year_id', woy.id)
      loadData()
    }
    const onClearWeekOfYearFilter = () => {
      inventoryAggregateApi.clearFilterNoLoad('week_of_year_id')
      loadData()
    }

    const onSelectSkuFilter = (sku) => {
      switch (sku.type) {
        case 'component':
          inventoryAggregateApi.addFilterNoLoad('sku_id', sku.id)
          inventoryAggregateApi.clearFilterNoLoad('sku_id_in')
          break
        case 'unit':
          inventoryAggregateApi.addFilterNoLoad('sku_id_in', sku.components.map(c => c.component_id))
          inventoryAggregateApi.clearFilterNoLoad('sku_id')
          break
      }
      loadData()
    }
    const onClearSkuFilter = () => {
      inventoryAggregateApi.clearFilterNoLoad('sku_id')
      inventoryAggregateApi.clearFilterNoLoad('sku_id_in')
      loadData()
    }

    return {
      chartData,
      chartOptions,
      inventoryAggregateLoading,
      weeksOfYear,
      weeksOfYearLoading,
      weekOfYearOriginalValue,
      onSelectWeekOfYearFilter,
      onClearWeekOfYearFilter,
      skuFilterRef,
      skus,
      skusLoading,
      onSelectSkuFilter,
      onClearSkuFilter,
    }
  },
}
</script>
